import React from "react";
import {
  Box, Typography, TextField, Button, Checkbox, FormControlLabel, MenuItem,
  Select, Radio, RadioGroup, IconButton,
} from "@mui/material";
import { Delete, AddCircleOutline } from "@mui/icons-material";
import { sanitizeNameInput } from "./validators";

// Shared between PatientRegisterWizard.js (initial telehealth registration)
// and MyPayments.js (ordering telehealth again post-registration) — kept in
// one place so the two entry points can never quietly drift out of sync on
// what fields/questions a telehealth order actually requires.

export const GENDER_OPTIONS = ["Male", "Female"];
export const RACE_OPTIONS = [
  "Asian", "Black or African American", "Hispanic or Latino", "White",
  "Native American", "Pacific Islander", "Other",
];

// Exact wording from the real report-generation app's Opioid Risk
// Questionnaire — used as dict keys downstream, so this must match verbatim.
export const PHENOTYPE_QUESTIONS = [
  "Age between 16 and 45 years ?",
  "Personal history of mental health disorders ?",
  "Family history of alcoholism ?",
  "Family history of illegal drug abuse ?",
  "Family history of prescription drug abuse ?",
  "Personal history of alcoholism ?",
  "Personal history of illegal drug abuse ?",
  "Personal history of prescription drug abuse ?",
  "History of preadolescent sexual abuse ?",
];
export const PHQ_QUESTIONS = [
  "Over the past two weeks, how often have you felt little interest or pleasure in doing things ?",
  "Over the past two weeks, how often have you felt down, depressed, or hopeless? ",
];
export const PHQ_OPTIONS = ["not at all", "several days", "more than half the days", "nearly everyday"];
export const PHQ_LABELS = { "not at all": "Not at all", "several days": "Several days", "more than half the days": "More than half the days", "nearly everyday": "Nearly every day" };
export const EMPTY_MED = { medication: "", side_effects: "", dose: "", frequency: "", efficacy: 3 };
export const EMPTY_ADDRESS = { line1: "", city: "", state: "", zip: "" };
export const EMPTY_CHILD = { first_name: "", last_name: "", date_of_birth: "", gender: "", race: "" };

export function Field({ label, required, hint, isDark, accentColor, children }) {
  const sub = isDark ? "rgba(255,255,255,0.55)" : "#475569";
  return (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.06em", color: sub, textTransform: "uppercase" }}>
          {label}{required && <Box component="span" sx={{ color: accentColor || "#637cdb", ml: 0.4 }}>*</Box>}
        </Typography>
        {hint && <Typography sx={{ fontSize: 12.5, color: sub }}>{hint}</Typography>}
      </Box>
      {children}
    </Box>
  );
}

export function AddressFields({ isDark, inputSx, value, onChange }) {
  return (
    <>
      <TextField hiddenLabel fullWidth placeholder="Street address" sx={{ ...inputSx, mb: 1 }}
        value={value.line1} onChange={(e) => onChange({ ...value, line1: e.target.value })} />
      <Box sx={{ display: "flex", gap: 1 }}>
        <TextField hiddenLabel placeholder="City" sx={{ ...inputSx, flex: 2 }}
          value={value.city} onChange={(e) => onChange({ ...value, city: e.target.value })} />
        <TextField hiddenLabel placeholder="State" sx={{ ...inputSx, flex: 1 }}
          value={value.state} onChange={(e) => onChange({ ...value, state: e.target.value })} />
        <TextField hiddenLabel placeholder="ZIP" sx={{ ...inputSx, flex: 1 }}
          value={value.zip} onChange={(e) => onChange({ ...value, zip: e.target.value })} />
      </Box>
    </>
  );
}

export function isUnder18(dobStr) {
  if (!dobStr) return false;
  const born = new Date(dobStr);
  const today = new Date();
  let age = today.getFullYear() - born.getFullYear();
  const m = today.getMonth() - born.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < born.getDate())) age--;
  return age < 18;
}

// The child-info + shipping/billing + questionnaire + medications section of
// a telehealth order — everything EXCEPT the parent's own account info,
// since the wizard collects that in an earlier step while MyPayments already
// has it from the logged-in account. Deliberately excludes any submit
// button/step-navigation chrome — callers own that.
export function TelehealthDetailsFields({
  isDark, inputSx, accentColor,
  child, setChild,
  shippingAddress, setShippingAddress, billingAddress, setBillingAddress,
  sameAsShipping, setSameAsShipping,
  phenotypeAnswers, setPhenotypeAnswers, phqAnswers, setPhqAnswers,
  medications, setMedications,
}) {
  const dobEntered = Boolean(child.date_of_birth);
  const dobTooOld = dobEntered && !isUnder18(child.date_of_birth);
  return (
    <Box>
      <Field label="Child's first name" required isDark={isDark} accentColor={accentColor}>
        <TextField hiddenLabel fullWidth sx={inputSx} value={child.first_name}
          onChange={(e) => setChild((p) => ({ ...p, first_name: sanitizeNameInput(e.target.value) }))} />
      </Field>
      <Field label="Child's last name" required isDark={isDark} accentColor={accentColor}>
        <TextField hiddenLabel fullWidth sx={inputSx} value={child.last_name}
          onChange={(e) => setChild((p) => ({ ...p, last_name: sanitizeNameInput(e.target.value) }))} />
      </Field>
      <Field label="Child's date of birth" required hint="Must be under 18 for KidSafe" isDark={isDark} accentColor={accentColor}>
        <TextField hiddenLabel type="date" fullWidth sx={inputSx} value={child.date_of_birth}
          onChange={(e) => setChild((p) => ({ ...p, date_of_birth: e.target.value }))}
          error={dobTooOld} helperText={dobTooOld ? "KidSafe is only available for children under 18." : ""} />
      </Field>
      <Box sx={{ display: "flex", gap: 1.5 }}>
        <Field label="Gender" required isDark={isDark} accentColor={accentColor}>
          <Select size="small" fullWidth displayEmpty value={child.gender} sx={inputSx}
            onChange={(e) => setChild((p) => ({ ...p, gender: e.target.value }))}>
            <MenuItem value="" disabled>Select…</MenuItem>
            {GENDER_OPTIONS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </Select>
        </Field>
        <Field label="Race / Ethnicity" required isDark={isDark} accentColor={accentColor}>
          <Select size="small" fullWidth displayEmpty value={child.race} sx={inputSx}
            onChange={(e) => setChild((p) => ({ ...p, race: e.target.value }))}>
            <MenuItem value="" disabled>Select…</MenuItem>
            {RACE_OPTIONS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </Select>
        </Field>
      </Box>

      <Field label="Shipping address" required hint="Your kit gets mailed here" isDark={isDark} accentColor={accentColor}>
        <AddressFields isDark={isDark} inputSx={inputSx} value={shippingAddress} onChange={setShippingAddress} />
      </Field>

      <FormControlLabel
        sx={{ mb: 1 }}
        control={<Checkbox size="small" checked={sameAsShipping} onChange={(e) => setSameAsShipping(e.target.checked)} />}
        label={<Typography sx={{ fontSize: 13, color: isDark ? "#cbd5e1" : "#334155" }}>Billing address is the same as shipping</Typography>}
      />
      {!sameAsShipping && (
        <Field label="Billing address" required isDark={isDark} accentColor={accentColor}>
          <AddressFields isDark={isDark} inputSx={inputSx} value={billingAddress} onChange={setBillingAddress} />
        </Field>
      )}

      {/* User-facing: one combined "Patient Health Questionnaire" section.
          Backend/Mongo storage stays split — phenotypeAnswers/phqAnswers
          are still submitted separately as phenotype_answers/phq_answers
          (TelehealthQuestionnaire), matching the schema proovereport's
          report-generation app expects (phenotypes_questions/
          phq_questions field names, unchanged). Only the display label
          changed — no backend field names touched. */}
      <Field label="Patient Health Questionnaire" required hint="Answer for your child" isDark={isDark} accentColor={accentColor}>
        {PHENOTYPE_QUESTIONS.map((q) => (
          <Box key={q} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.6, borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(10,14,39,0.06)"}` }}>
            <Typography sx={{ fontSize: 13, color: isDark ? "#cbd5e1" : "#334155", flex: 1, pr: 2 }}>{q}</Typography>
            <RadioGroup row value={phenotypeAnswers[q] || ""} onChange={(e) => setPhenotypeAnswers((p) => ({ ...p, [q]: e.target.value }))}>
              <FormControlLabel value="Yes" control={<Radio size="small" />} label="Yes" />
              <FormControlLabel value="No" control={<Radio size="small" />} label="No" />
            </RadioGroup>
          </Box>
        ))}
        {PHQ_QUESTIONS.map((q) => (
          <Box key={q} sx={{ mt: 1.5 }}>
            <Typography sx={{ fontSize: 13, color: isDark ? "#cbd5e1" : "#334155", mb: 0.5 }}>{q}</Typography>
            <Select size="small" fullWidth displayEmpty value={phqAnswers[q] || ""} onChange={(e) => setPhqAnswers((p) => ({ ...p, [q]: e.target.value }))} sx={inputSx}>
              <MenuItem value="" disabled>Select one…</MenuItem>
              {PHQ_OPTIONS.map((o) => <MenuItem key={o} value={o}>{PHQ_LABELS[o]}</MenuItem>)}
            </Select>
          </Box>
        ))}
      </Field>

      <Field label="Current medications" hint="Optional — add any your child currently takes" isDark={isDark} accentColor={accentColor}>
        {medications.map((med, i) => (
          <Box key={i} sx={{ display: "flex", gap: 1, mb: 1, alignItems: "flex-start" }}>
            <TextField hiddenLabel placeholder="Medication" sx={{ ...inputSx, flex: 2 }}
              value={med.medication} onChange={(e) => setMedications((prev) => prev.map((m, idx) => idx === i ? { ...m, medication: e.target.value } : m))} />
            <TextField hiddenLabel placeholder="Side effects" sx={{ ...inputSx, flex: 2 }}
              value={med.side_effects} onChange={(e) => setMedications((prev) => prev.map((m, idx) => idx === i ? { ...m, side_effects: e.target.value } : m))} />
            <TextField hiddenLabel placeholder="Dose" sx={{ ...inputSx, flex: 1 }}
              value={med.dose} onChange={(e) => setMedications((prev) => prev.map((m, idx) => idx === i ? { ...m, dose: e.target.value } : m))} />
            <IconButton size="small" onClick={() => setMedications((prev) => prev.filter((_, idx) => idx !== i))}>
              <Delete fontSize="small" />
            </IconButton>
          </Box>
        ))}
        <Button size="small" startIcon={<AddCircleOutline />} onClick={() => setMedications((prev) => [...prev, { ...EMPTY_MED }])} sx={{ textTransform: "none" }}>
          Add medication
        </Button>
      </Field>
    </Box>
  );
}
